import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetDepenseByIdQuery } from 'actions/depenses/queries';
import { useGetPiecesComptablesQuery } from 'actions/pieces/queries';
import { useRattacherPieceMutation, useDetacherPieceMutation } from 'actions/pieces/mutations';
import HeaderFirst from 'utils/HeaderFirst';
import InputDetail from 'components/Inputs/InputDetail';
import useDepensesData from './useDepensesData';
import './client.css';
import FilePreview from 'components/FilePreview/FilePreview';
import { useGetProduitsQuery } from 'actions/produits/queries';
import { useGetCodesQuery, useGetEcrituresManuellesQuery } from 'actions/comptabilite/queries';
import AutocompleteSelect from 'components/AutocompleteSelect/AutocompleteSelect';
import { toast } from 'react-toastify';

const formatDate = (dateString) => {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Date(dateString)
    .toLocaleDateString('fr-FR', options)
    .replace(',', '');
};

export default function DepenseDetail() {
  const { id } = useParams();
  const { id: depense_id } = useParams();
  const { clientID: dossier_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = new URLSearchParams(location.search).get('edit') === 'true';
  const isManual = String(id || "").startsWith("MAN-");
  const manualId = isManual ? Number(String(id).replace("MAN-", "")) : null;

  const { data: ecrituresData, isLoading: isLoadingEcritures } = useGetEcrituresManuellesQuery(
    { dossier_id },
    { skip: !isManual || !dossier_id }
  );

  const {
    data: getDepense,
    error: depenseError,
    isLoading: isLoadingDepense,
    refetch: refetchDepense,
  } = useGetDepenseByIdQuery({ id, dossier_id }, { skip: !id || isManual });

  const manualEcriture = isManual
    ? (ecrituresData?.ecritures || []).find((e) => e.id === manualId)
    : null;

  const rawDepense = getDepense?.data?.[0] || getDepense?.data || (Array.isArray(getDepense) ? getDepense[0] : getDepense);

  const depenseDetail = isManual
    ? (manualEcriture
        ? {
            id,
            intitule: manualEcriture.libelle_original || manualEcriture.libelle || "Écriture manuelle",
            description: manualEcriture.libelle || "",
            prix: Number(manualEcriture.debit || manualEcriture.credit || 0),
            montant_total: Number(manualEcriture.debit || manualEcriture.credit || 0),
            updated_at: manualEcriture.date,
            date_transaction: manualEcriture.date,
            fournisseurs_details: { nom: "Saisie manuelle" },
            produits_associes: [],
          }
        : {})
    : (rawDepense || {});
  const { data: produitsData } = useGetProduitsQuery({ dossier_id });
  const { data: codesData } = useGetCodesQuery({ dossier_client: dossier_id });

  // Comptes proposables pour un achat : classe 6 (charges) + classe 2 (immobilisations)
  const rawCodes = Array.isArray(codesData)
    ? codesData
    : codesData?.data || codesData?.results || [];
  const accountOptions = rawCodes
    .filter((c) => {
      const s = String(c.code ?? '');
      return s.startsWith('6') || s.startsWith('2');
    })
    .map((c) => ({ id: Number(c.code), nom: c.combine || `${c.code} - ${c.libelle}` }));

  const { handleUpdateDepense } = useDepensesData(depense_id);

  // EC-21 : Rattachement pièce GED
  const [selectedPieceId, setSelectedPieceId] = useState("");
  const { data: piecesData, refetch: refetchPieces } = useGetPiecesComptablesQuery({ dossier_id }, { skip: !dossier_id });
  const [rattacherPiece, { isLoading: isAttaching }] = useRattacherPieceMutation();
  const [detacherPiece, { isLoading: isDetaching }] = useDetacherPieceMutation();
  const piecesGed = piecesData?.documents || piecesData?.data || (Array.isArray(piecesData) ? piecesData : []);
  const pieceRattachee =
    depenseDetail?.piece_comptable_details ||
    piecesGed.find((p) => p.id === (depenseDetail?.piece_comptable?.id || depenseDetail?.piece_comptable));

  const handleRattacherPiece = async (pieceIdToAttach) => {
    const pId = pieceIdToAttach || selectedPieceId;
    if (!pId) {
      toast.warning("Veuillez sélectionner une pièce dans la liste.");
      return;
    }
    const cleanPieceId = typeof pId === 'object' ? pId.id : Number(pId);
    try {
      const res = await rattacherPiece({ piece_id: cleanPieceId, target_type: "sortie", target_id: id }).unwrap();
      toast.success(res?.message || "Pièce rattachée avec succès.");
      setSelectedPieceId("");
      if (refetchDepense) await refetchDepense();
      if (refetchPieces) await refetchPieces();
      window.dispatchEvent(new CustomEvent('compta:refresh'));
    } catch (err) {
      const msg = err?.data?.message || err?.data?.detail || err?.message || "Erreur lors du rattachement de la pièce.";
      toast.error("Erreur : " + msg);
    }
  };

  const handleDetacherPiece = async () => {
    const rawId = pieceRattachee?.id || (typeof depenseDetail?.piece_comptable === 'object' ? depenseDetail?.piece_comptable?.id : depenseDetail?.piece_comptable);
    const pId = rawId ? Number(rawId) : undefined;
    try {
      const res = await detacherPiece({ piece_id: pId, target_type: "sortie", target_id: id }).unwrap();
      toast.success(res?.message || "Pièce détachée.");
      if (refetchDepense) await refetchDepense();
      if (refetchPieces) await refetchPieces();
      window.dispatchEvent(new CustomEvent('compta:refresh'));
    } catch (err) {
      const msg = err?.data?.message || err?.data?.detail || err?.message || "Erreur lors du détachement de la pièce.";
      toast.error("Erreur : " + msg);
    }
  };

  const [updateData, setUpdateData] = useState({
    date: '',
    intitule: '',
    description: '',
    quantite: '',
    prix: '',
    produits_associes: [],
  });
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  useEffect(() => {
    if (depenseDetail && isEditing) {
      // Assuming produitsData is already loaded and available
      const enrichedProduits = (depenseDetail.produits_associes || []).map(
        (prodAssoc) => {
          // Attempt to enrich each product with additional details if available
          const productDetail =
            produitsData?.data?.find((p) => p.id === prodAssoc.produit) || {};

          return {
            id: prodAssoc.produit, // Keep the product ID
            nom: productDetail.nom || prodAssoc.nom || 'Inconnu',
            quantite: prodAssoc.quantite?.toString() || '', // Convert quantite to string
            prix: prodAssoc.prix?.toString() || '', // Convert prix to string
            code_comptable: prodAssoc.code_comptable ?? null,
          };
        }
      );

      setUpdateData({
        date: depenseDetail.updated_at_at || '',
        intitule: depenseDetail.intitule || '',
        description: depenseDetail.description || '',
        produits_associes: enrichedProduits,
      });
    }
  }, [depenseDetail, isEditing, produitsData]);

  // Recalculer le total automatiquement quand les produits changent
  useEffect(() => {
    if (updateData.produits_associes && updateData.produits_associes.length > 0) {
      const total = updateData.produits_associes.reduce((sum, prod) => {
        const quantite = parseFloat(prod.quantite) || 0;
        const prix = parseFloat(prod.prix) || 0;
        return sum + (quantite * prix);
      }, 0);
      setCalculatedTotal(total);
    } else {
      setCalculatedTotal(0);
    }
  }, [updateData.produits_associes]);

  const handleQuantiteChange = (index, newQuantite) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].quantite = newQuantite.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handlePrixChange = (index, newPrix) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].prix = newPrix.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleProduitChange = (index, value) => {
    // Ne garder que les ids numeriques (selection dans la liste)
    if (typeof value !== 'number') return;
    const prod = (produitsData?.data || []).find((p) => p.id === value);
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        id: value,
        nom: prod?.nom || updatedProduits[index].nom,
        code_comptable:
          updatedProduits[index].code_comptable ?? prod?.code_comptable ?? null,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleCompteChange = (index, value) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        code_comptable: value,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const setDepenseDetail = async (e) => {
    e.preventDefault();
    const submitData = {
      ...updateData,
      montant_total: calculatedTotal, // Envoyer le total calculé
      produits_associes: updateData.produits_associes.map((prod) => {
        const code = String(prod.code_comptable ?? '').replace(/\D/g, '');
        return {
          produit: prod.id, // Assuming prod.id contains the product ID.
          quantite: parseFloat(prod.quantite), // Ensure quantite is a number.
          prix: prod.prix.toString(), // Ensure prix is a string.
          code_comptable: code ? Number(code) : null,
        };
      }),
    };

    // Call the update function with the prepared data.
    await handleUpdateDepense(id, submitData);
    navigate(-1); // Redirect after successful update.
  };

  const nom = (
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">Modification de sortie: {depenseDetail?.id}</h4>
    </div>
  );

  const isLoading = isManual ? isLoadingEcritures : isLoadingDepense;
  const isError = !isManual && depenseError;

  if (isLoading) return <div style={{ padding: "30px", background: "#ffffff", borderRadius: "12px", margin: "20px" }}>Chargement des détails de dépense...</div>;
  if (isError) return <div style={{ padding: "30px", background: "#ffffff", borderRadius: "12px", margin: "20px", color: "#dc2626" }}>Une erreur est survenue pour la dépense.</div>;
  if (!depenseDetail || (!depenseDetail.id && !depenseDetail.intitule && depenseDetail.prix === undefined)) {
    return <div style={{ padding: "30px", background: "#ffffff", borderRadius: "12px", margin: "20px" }}>Sortie introuvable ou non disponible.</div>;
  }

  return (
    <div className="depense-detail-container">
      <div className="d-flex align-items-center">
        <HeaderFirst
          nom={nom}
          description={
            <div>Modifiez les informations de cette sortie. Tous les calculs comptables seront mis à jour automatiquement.</div>
          }
        />
      </div>
      <form
        className="form-layout"
        onSubmit={setDepenseDetail}
        style={{ padding: '15px' }}
      >
        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Fournisseur"
              name="fournisseur"
              disabled
              value={depenseDetail?.fournisseurs_details?.nom}
              onChange={(e) =>
                setDepenseDetail({
                  ...depenseDetail,
                  fournisseur: e.target.value,
                })
              }
              className={'custom-input'}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Date"
              name="date"
              type="text"
              disabled
              value={formatDate(depenseDetail.updated_at)}
              onChange={(e) =>
                setDepenseDetail({ ...depenseDetail, date: e.target.value })
              }
              className={'custom-input'}
            />
          </div>
        </div>

        {/* Add other input fields as necessary */}
        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Intitulé"
              name="intitule"
              value={updateData.intitule}
              onChange={(e) =>
                setUpdateData({ ...updateData, intitule: e.target.value })
              }
              className={'custom-input'}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Description"
              name="description"
              value={updateData.description}
              onChange={(e) =>
                setUpdateData({ ...updateData, description: e.target.value })
              }
              className={'custom-input'}
            />
          </div>
        </div>
        {updateData.produits_associes &&
        updateData.produits_associes.length > 0 ? (
          updateData.produits_associes.map((prod, index) => (
            <div key={`${prod.id}-${index}`} className="row flex-row justify-content-between" style={{ marginBottom: '15px' }}>
              <div className="col-4">
                <AutocompleteSelect
                  label="Produit"
                  options={produitsData?.data || []}
                  value={prod.id}
                  onChange={(value) => handleProduitChange(index, value)}
                  placeholder="Sélectionnez un produit"
                />
              </div>
              <div className="col-2">
                <InputDetail
                  label="Quantité"
                  name={`quantite-${prod.id}`}
                  type="number"
                  value={prod.quantite}
                  onChange={(e) =>
                    handleQuantiteChange(index, parseFloat(e.target.value) || 0)
                  }
                  className={'custom-input'}
                />
              </div>
              <div className="col-3">
                <InputDetail
                  label="Prix"
                  name={`prix-${prod.id}`}
                  type="number"
                  value={prod.prix}
                  onChange={(e) => handlePrixChange(index, parseFloat(e.target.value) || 0)}
                  className={'custom-input'}
                />
              </div>
              <div className="col-3">
                <AutocompleteSelect
                  label="Compte comptable"
                  options={accountOptions}
                  value={prod.code_comptable ?? null}
                  onChange={(value) => handleCompteChange(index, value)}
                  createText="Utiliser"
                  onCreateNew={async (name) => {
                    const digits = String(name).replace(/\D/g, '');
                    if (!digits) return null;
                    return { id: Number(digits), nom: String(name).trim() };
                  }}
                  placeholder="Ex: 245000 (vide = compte du produit)"
                />
              </div>
            </div>
          ))
        ) : (
          <p>No products associated.</p>
        )}
        
        {/* EC-21 : Rattachement pièce GED */}
        <div className="pieceContainer col-md-12 mb-3">
          <p style={{ color: '#009688', fontWeight: 'bold' }} className="form-label">
            Pièce justificative (GED)
          </p>
          {pieceRattachee ? (
            <div className="card py-2" style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
              <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <span style={{ fontWeight: 700, color: '#166534', marginRight: 8 }}>📎 Pièce liée :</span>
                  <strong>{pieceRattachee.titre || pieceRattachee.title}</strong>
                  {pieceRattachee.partenaire ? ` : ${pieceRattachee.partenaire}` : ""}
                  {pieceRattachee.montant ? ` : ${Number(pieceRattachee.montant).toLocaleString('fr-FR')} FCFA` : ""}
                  {(pieceRattachee.fileUrl || pieceRattachee.fichier) && (
                    <a
                      href={pieceRattachee.fileUrl || pieceRattachee.fichier}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ms-3 badge text-white"
                      style={{ background: '#009688', color: '#ffffff', textDecoration: 'none', padding: '6px 12px', borderRadius: 12 }}
                    >
                      <span style={{ color: '#ffffff', fontWeight: 600 }}>Ouvrir le fichier</span>
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ fontWeight: 600, padding: '5px 14px', borderRadius: '20px' }}
                  disabled={isDetaching}
                  onClick={handleDetacherPiece}
                >
                  ✕ {isDetaching ? "Détachement..." : "Détacher"}
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <select
                className="form-select"
                style={{ flex: 1, minWidth: 260 }}
                value={selectedPieceId}
                onChange={(e) => setSelectedPieceId(e.target.value)}
              >
                <option value="">Sélectionner une pièce de la GED...</option>
                {piecesGed.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.titre || p.title} : {p.partenaire || "N/A"} : {Number(p.montant || 0).toLocaleString('fr-FR')} FCFA
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                style={{
                  background: '#009688',
                  borderColor: '#009688',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  padding: '7px 18px',
                  borderRadius: '20px',
                }}
                disabled={!selectedPieceId || isAttaching}
                onClick={() => handleRattacherPiece(selectedPieceId)}
              >
                📎 {isAttaching ? "Rattachement..." : "Rattacher"}
              </button>
            </div>
          )}
        </div>

        {/* Aperçu de la pièce jointe */}
        <div className="input-row d-flex">
          {(() => {
            const previewPath = pieceRattachee?.fileUrl || pieceRattachee?.fichier || depenseDetail?.piece_joint || depenseDetail?.justificatif_file;
            if (previewPath) {
              return (
                <div className="pieceContainer col-md-12 mb-3">
                  <p style={{ color: '#009688', fontWeight: 'bold' }} className="form-label">
                    Aperçu de la pièce jointe
                  </p>
                  <a href={previewPath} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <div className="card py-3">
                      <div className="card-body d-flex align-items-center justify-content-center text-center">
                        <FilePreview filePath={previewPath} />
                      </div>
                    </div>
                  </a>
                </div>
              );
            }
            return (
              <div className="pieceContainer col-md-12 mb-3">
                <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 13 }}>
                  Aucune pièce justificative jointe pour cette transaction.
                </p>
              </div>
            );
          })()}
        </div>

        <div className="form-navigation-buttons">
          <button type="submit" className="next-button">
            Modifier
          </button>
        </div>
      </form>
    </div>
  );
}
